package aexorsa

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout
import react.FC
import js.reflect.unsafeCast
import react.useEffect
import react.useEffectOnce
import react.useRef
import react.useState
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import web.autofill.*
import web.cssom.ClassName
import web.form.FormData
import web.form.FormMethod
import web.form.post
import web.html.*
import web.dom.ElementId
import web.http.Headers
import web.http.RequestInit
import web.http.RequestMethod
import web.http.POST
import web.http.fetch
import web.url.URLSearchParams

val SignupForm = FC {
    var busy by useState(false)
    var saved by useState(false)
    var cooldown by useState(0)
    val emailInput = useRef<HTMLInputElement>(null)
    var message by useState("")
    var failed by useState(false)
    val submitting = useRef(false)
    val scope = useRef<CoroutineScope>(null)
    useEffectOnce {
        scope.current = this
        awaitCancellation()
    }

    useEffect(cooldown) {
        if (cooldown > 0) { delay(1_000); cooldown -= 1 }
    }

    div {
        className = ClassName("launch-updates")
        id = ElementId("launch-updates")
        form {
            className = ClassName("signup-form")
            action = unsafeCast("/api/signups")
            method = FormMethod.post
            ariaLabel = "Launch updates"
            onSubmit = { event ->
                event.preventDefault()
                val submitScope = scope.current
                if (submitting.current != true && cooldown == 0 && submitScope != null) {
                    submitting.current = true
                    busy = true
                    failed = false
                    message = ""
                    val data = FormData(event.currentTarget)
                    val fields = URLSearchParams().apply {
                        set("email", data.get("email").toString())
                        set("consentVersion", "double-opt-in-2026-09-12")
                        set("website", data.get("website").toString())
                    }
                    submitScope.launch {
                        try {
                            val response = withTimeout(15_000) {
                                fetch("/api/signups", RequestInit(
                                    method = RequestMethod.POST,
                                    headers = Headers().apply { set("Accept", "application/json") },
                                    body = fields,
                                ))
                            }
                            if (response.ok) saved = true
                            if (response.ok) cooldown = 60
                            if (response.status.toInt() == 429) cooldown = (response.headers.get("Retry-After")?.toIntOrNull() ?: 60).coerceIn(1, 600)
                            failed = !response.ok
                            message = when {
                                response.ok -> "If this address needs confirmation, we'll send a link. Check your inbox and spam folder."
                                response.status.toInt() == 400 -> "Please check your email address and try again."
                                response.status.toInt() == 429 -> "Too many attempts. Please wait a few minutes and try again."
                                else -> "We couldn't request confirmation. Please try again shortly."
                            }
                        } catch (_: TimeoutCancellationException) {
                            failed = true
                            message = "That took too long. Please try again."
                        } catch (error: Exception) {
                            if (error is CancellationException) throw error
                            failed = true
                            message = "We couldn't connect. Check your connection and try again."
                        } finally {
                            submitting.current = false
                            busy = false
                        }
                    }
                }
            }
            label {
                className = ClassName("visually-hidden")
                htmlFor = ElementId("signup-email")
                +"Email address"
            }
            div {
                className = ClassName("signup-fields")
                input {
                    id = ElementId("signup-email")
                    name = "email"
                    type = InputType.email
                    autoComplete = AutoFillContactField.email
                    placeholder = "Your email address"
                    required = true
                    maxLength = 254
                    ref = emailInput
                    disabled = busy
                    readOnly = saved
                    ariaDescribedBy = ElementId("signup-consent signup-status")
                }
                button {
                    type = ButtonType.submit
                    disabled = busy || cooldown > 0
                    +(if (busy) "Requesting..." else if (cooldown > 0) "Wait ${cooldown}s" else if (saved) "Send another confirmation" else "Keep me posted")
                }
            }
            input {
                type = InputType.hidden
                name = "consentVersion"
                value = "double-opt-in-2026-09-12"
            }
            div {
                className = ClassName("signup-trap")
                ariaHidden = true
                label { htmlFor = ElementId("signup-website"); +"Leave this field empty" }
                input {
                    id = ElementId("signup-website")
                    name = "website"
                    type = InputType.text
                    tabIndex = -1
                    autoComplete = AutoFillBase.off
                }
            }
            p {
                id = ElementId("signup-consent")
                className = ClassName("signup-consent")
                +"Request a confirmation email. Launch updates begin only after you\u00A0confirm. "
                a { href = "mailto:support@aexorsa.com?subject=Unsubscribe"; +"Leave\u00A0the\u00A0list" }
                +"\u00A0anytime. "
                a { href = "/privacy/"; +"Privacy" }
            }
            if (saved) {
                button {
                    className = ClassName("signup-secondary")
                    type = ButtonType.button
                    disabled = busy
                    onClick = {
                        saved = false
                        failed = false
                        message = ""
                        emailInput.current?.let { it.value = ""; it.focus() }
                    }
                    +"Use a different address"
                }
            }
            p {
                id = ElementId("signup-status")
                className = ClassName(if (failed) "signup-status signup-error" else "signup-status")
                role = AriaRole.status
                +message
            }
        }
    }
}
